import { getEnviroment } from 'env';
import { AxiosRequest } from '../common/AxiosRequest';
import { HALLink } from '../common/HALLinkResolver';
import { AccessToken } from "@okta/okta-auth-js";
import axios from 'axios';
import { defaultAxiosOptions } from '../common/DefaultAxiosOptions';

export type PredefinedCustomerCommand = {
    commandId: string;
    label: string;
    customerId: number;
    sortOrder: number;
    canProfileId: number;
    minimumFirmwareVersion: number;
    maximumFirmwareVersion: number;
    groupId: number;
    groupLabel: string;
    link: HALLink;
};

export type PredefinedCustomerCommandRequest = {
    commandId: string;
    serialNumbers: string[];
};

export type DeviceReplaceRequest = {
  fromSerialNumber: string;
  toSerialNumber: string;
  outputMode: boolean;
  inputFiltering: boolean;
  skipOwnerCheck?: boolean;
  skipDuplicateReplacementCheck?: boolean;
};

export enum ReplacementStatus {
  SUCCESS = 'SUCCESS',
  INVALID_REQUEST = 'INVALID_REQUEST',
  REPLACEMENT_REPEAT = 'REPLACEMENT_REPEAT',
  OWNER_CHECK_FAILED = 'OWNER_CHECK_FAILED',
  DEVICE_TYPE_INCOMPATIBLE = 'DEVICE_TYPE_INCOMPATIBLE',
  FIRMWARE_NOT_SUPPORTED = 'FIRMWARE_NOT_SUPPORTED',
  NOT_ALLOWED = 'NOT_ALLOWED',
  TIMEOUT = 'TIMEOUT',
  INTERNAL_ERROR = 'INTERNAL_ERROR'
}

export type DeviceReplaceOutcome =
  | 'success'
  | 'replacementRepeat'
  | 'ownerCheckFailed'
  | 'other';

export type DeviceReplaceResult = {
  outcome: DeviceReplaceOutcome;
  message: string;
  trackingId?: string;
  reason?: string;
};

type RawReplaceResponseBody = {
  status?: string | number;
  code?: string;
  message?: string;
  trackingId?: string;
  properties?: {
    trackingId?: string;
  };
};

function isReplacementResultBody(data: unknown): data is RawReplaceResponseBody {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const body = data as RawReplaceResponseBody;
  return body.code !== undefined || body.status !== undefined;
}

function normalizeDeviceReplaceResult(data: RawReplaceResponseBody): DeviceReplaceResult {
  const trackingId = data.trackingId ?? data.properties?.trackingId;
  const message = data.message ?? 'Device replacement failed';

  if (data.status === ReplacementStatus.SUCCESS) {
    return { outcome: 'success', message, trackingId };
  }

  const legacyStatusCode =
    data.status === ReplacementStatus.REPLACEMENT_REPEAT ? 'replacement_repeat'
      : data.status === ReplacementStatus.OWNER_CHECK_FAILED ? 'owner_check_failed'
        : undefined;

  const code = data.code ?? legacyStatusCode;

  if (code === 'replacement_repeat') {
    return { outcome: 'replacementRepeat', message, trackingId, reason: 'replacement_repeat' };
  }

  if (code === 'owner_check_failed') {
    return { outcome: 'ownerCheckFailed', message, trackingId, reason: 'owner_check_failed' };
  }

  const reason = code ?? (data.status !== undefined ? String(data.status) : 'unknown');
  return { outcome: 'other', message, trackingId, reason };
}

const telematicsDeviceApiUrl = getEnviroment().endpoints.telematicsDevice + '/telematics-devices';
const predefinedCommandsApiUrl = getEnviroment().endpoints.telematicsDevice + '/commands/predefined';

function getPredefinedCustomerCommands(accessToken: AccessToken | undefined): Promise<PredefinedCustomerCommand[]> {
    return AxiosRequest.get(
        accessToken,
        `${predefinedCommandsApiUrl}`,
        result => {
            return result.data as PredefinedCustomerCommand[];
        }
    );
}

function executePredefinedCustomerCommand(accessToken: AccessToken | undefined, serialNumber: string, commandId: string) {
    const serialNumbers: string[] = [];
    serialNumbers.push(serialNumber);

    const predefinedCustomerCommandRequest: PredefinedCustomerCommandRequest = {
        commandId,
        serialNumbers
    };

    return AxiosRequest.post(
        accessToken,
        `${predefinedCommandsApiUrl}`,
        predefinedCustomerCommandRequest
    );
}

function executeReplace(
  accessToken: AccessToken | undefined,
  fromSerialNumber: string,
  toSerialNumber: string,
  outputMode: boolean,
  inputFiltering: boolean,
  skipOwnerCheck: boolean = false,
  skipDuplicateReplacementCheck: boolean = false
): Promise<DeviceReplaceResult> {
  if (!accessToken) {
    throw new Error("User is not authenticated");
  }

  const deviceReplaceApiUrl = telematicsDeviceApiUrl + '/replacements';

  const deviceReplaceRequest: DeviceReplaceRequest = {
    fromSerialNumber,
    toSerialNumber,
    outputMode,
    inputFiltering,
    skipOwnerCheck,
    skipDuplicateReplacementCheck
  };

  return axios
    .post(deviceReplaceApiUrl, deviceReplaceRequest, defaultAxiosOptions(accessToken))
    .then(response => normalizeDeviceReplaceResult(response.data))
    .catch(error => {
      const data = error.response?.data;
      if (isReplacementResultBody(data)) {
        return normalizeDeviceReplaceResult(data);
      }

      throw new Error(error.response?.data?.message || error.message || 'An unexpected error occurred while trying to replace device');
    });
}

function deviceReboot(accessToken: AccessToken | undefined,
                serialNumber: string) {
    const inputConfigApiUrl = `${telematicsDeviceApiUrl}/${serialNumber}/reboot`;

    return AxiosRequest.post(accessToken, `${inputConfigApiUrl}`);
}

export const TelematicsDeviceConfigurationApi = {
    getPredefinedCustomerCommands,
    executePredefinedCustomerCommand,
    executeReplace,
    deviceReboot
};
